import makeStyles from '@mui/styles/makeStyles';
import { Field, FastField } from 'formik';
import React from 'react';

import { toast } from 'react-toastify';
import { useFileUpload } from '../../hooks/api/files/useFileUpload';
import UploadProgress from './UploadProgress';

import Autocomplete from 'apollo-react/components/Autocomplete';
import Checkbox from 'apollo-react/components/Checkbox';
import DatePicker from 'apollo-react/components/DatePicker';
import DateRangePicker from 'apollo-react/components/DateRangePicker';
import FileInput from 'apollo-react/components/FileInput';
import FileUploader from 'apollo-react/components/FileUploader';
import FormGroup from 'apollo-react/components/FormGroup';
import FormHelperText from 'apollo-react/components/FormHelperText';
import PasswordCriteria from 'apollo-react/components/PasswordCriteria';
import PasswordInput from 'apollo-react/components/PasswordInput';
import PhoneNumberInput from 'apollo-react/components/PhoneNumberInput';
import RadioGroup from 'apollo-react/components/RadioGroup';
import Select from 'apollo-react/components/Select';
import Slider from 'apollo-react/components/Slider';
import Switch from 'apollo-react/components/Switch';
import TextField from 'apollo-react/components/TextField';
import Typography from './StyledTypography';

export const styles = {
  topSpacer: {
    marginTop: 16,
  },
  phoneNumber: {
    margin: '16px 0',
  },
  passwordWrapper: {
    display: 'flex',
    alignItems: 'start',
  },
  passwordComplexity: {
    marginTop: 54,
  },
  thumb: {
    width: 200,
    height: 200,
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    },
  },
};

const useStyles = makeStyles(styles);

export const FormikTextField = ({ name, helperText, ...rest }) => (
  <FastField name={name}>
    {({ field, form: { errors, touched } }) => (
      <TextField
        helperText={(touched[name] && errors[name]) || helperText}
        error={!!(touched[name] && errors[name])}
        {...field}
        {...rest}
      />
    )}
  </FastField>
);

export const FormikRadioGroup = ({ name, helperText, ...rest }) => {
  const classes = useStyles();

  return (
    <FastField name={name}>
      {({ field, form: { errors, touched } }) => (
        <div className={classes.topSpacer}>
          <RadioGroup
            aria-label={name}
            {...field}
            onBlur={(e) => {
              e.target.name = name;
              field.onBlur(e);
            }}
            error={touched[name] && !!errors[name]}
            helperText={(touched[name] && errors[name]) || helperText}
            {...rest}
          />
        </div>
      )}
    </FastField>
  );
};

export const FormikCheckbox = ({ name, ...rest }) => (
  <FastField name={name}>
    {({ field: { value, ...field }, form: { errors, touched } }) => (
      <FormGroup>
        <Checkbox checked={value} value={name} {...field} {...rest} />
        {touched[name] && errors[name] && (
          <FormHelperText error>{errors[name]}</FormHelperText>
        )}
      </FormGroup>
    )}
  </FastField>
);

export const FormikSwitch = ({ name, ...rest }) => (
  <FastField name={name}>
    {({ field: { value, ...field } }) => (
      <Switch
        checked={value}
        value={name}
        {...field}
        onBlur={(e) => {
          e.target.name = name;
          field.onBlur(e);
        }}
        {...rest}
      />
    )}
  </FastField>
);

export const FormikSelect = ({ name, helperText, ...rest }) => (
  <FastField name={name}>
    {({ field, form: { errors, touched } }) => (
      <Select
        helperText={(touched[name] && errors[name]) || helperText}
        error={!!(touched[name] && errors[name])}
        {...field}
        {...rest}
      />
    )}
  </FastField>
);

export const FormikAutocomplete = ({
  name,
  helperText,
  isField = false,
  ...rest
}) => {
  const FieldComponent = isField ? Field : FastField;

  return (
    <FieldComponent name={name}>
      {({ field: { onChange }, form: { errors, touched } }) => (
        <Autocomplete
          helperText={(touched[name] && errors[name]) || helperText}
          onChange={(event, value) => {
            onChange({ target: { value, name } });
          }}
          error={!!(touched[name] && errors[name])}
          {...rest}
        />
      )}
    </FieldComponent>
  );
};

export const FormikPhoneInput = ({ name, helperText, ...rest }) => {
  const classes = useStyles();

  return (
    <FastField name={name}>
      {({ field: { onChange }, form: { errors, touched } }) => (
        <PhoneNumberInput
          helperText={(touched[name] && errors[name]) || helperText}
          defaultCountry="us"
          error={!!(touched[name] && errors[name])}
          onChange={(value) => {
            onChange({ target: { name, value } });
          }}
          className={classes.phoneNumber}
          {...rest}
        />
      )}
    </FastField>
  );
};

export const FormikDatePicker = ({ name, helperText, ...rest }) => (
  <FastField name={name}>
    {({ field: { value, onChange }, form: { errors, touched } }) => (
      <DatePicker
        onChange={(value) => {
          onChange({ target: { name, value } });
        }}
        value={value}
        helperText={(touched[name] && errors[name]) || helperText}
        error={!!(touched[name] && errors[name])}
        {...rest}
      />
    )}
  </FastField>
);

export const FormikDateRangePicker = ({
  name,
  label,
  startProps = {},
  endProps = {},
  ...rest
}) => {
  const classes = useStyles();
  return (
    <FastField name={name}>
      {({ field: { onChange, ...input }, form: { errors, touched } }) => (
        <div className={classes.topSpacer}>
          <Typography variant="bodySmall">{label}</Typography>
          <DateRangePicker
            name={name}
            onChange={(value, inputValue) => {
              onChange({ target: { value, inputValue, name } });
            }}
            startProps={{
              ...startProps,
              error: !!(touched[name] && errors[name]),
              helperText:
                (touched[name] && errors[name]) || startProps.helperText,
            }}
            endProps={{
              ...endProps,
              error: !!(touched[name] && errors[name]),
              helperText:
                (touched[name] && errors[name]) || endProps.helperText,
            }}
            {...input}
            {...rest}
          />
        </div>
      )}
    </FastField>
  );
};

export const FormikSlider = ({ name, label, unit, ...rest }) => {
  const classes = useStyles();

  return (
    <FastField name={name}>
      {({ field, helperText, form: { errors, touched } }) => (
        <div className={classes.topSpacer}>
          <Slider
            name={name}
            {...field}
            {...rest}
            label={`${label} (${field.value}${unit})`}
            helperText={(touched[name] && errors[name]) || helperText}
            error={touched[name] && !!errors[name]}
            onChange={(e, value) => field.onChange({ target: { name, value } })}
            onBlur={(e, value) => field.onBlur({ target: { name, value } })}
          />
        </div>
      )}
    </FastField>
  );
};

export const FormikPasswordInput = ({
  name,
  helperText,
  fullWidth,
  showComplexity,
  ...rest
}) => {
  const classes = useStyles();

  return (
    <FastField name={name}>
      {({ field, form: { errors, touched } }) => (
        <div className={classes.passwordWrapper}>
          <PasswordInput
            helperText={(touched[name] && errors[name]) || helperText}
            error={!!(touched[name] && errors[name])}
            fullWidth={fullWidth}
            {...field}
            {...rest}
          />
          {showComplexity && (
            <PasswordCriteria
              className={classes.passwordComplexity}
              fullWidth={fullWidth}
              value={field.value}
            />
          )}
        </div>
      )}
    </FastField>
  );
};

export const FormikPictureInput = ({ name, helperText, ...rest }) => {
  const classes = useStyles();

  return (
    <FastField name={name}>
      {({ field, form: { errors, touched } }) => (
        <>
          <FileInput
            onChange={(file) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                field.onChange({
                  target: {
                    name,
                    value: { file, thumb: reader.result },
                  },
                });
              };
              reader.readAsDataURL(file);
            }}
            helperText={(touched[name] && errors[name]) || helperText}
            error={!!(touched[name] && errors[name])}
            {...rest}
          />
          {field.value && (
            <div className={classes.thumb}>
              <img alt="Uploaded" src={field.value.thumb} />
            </div>
          )}
        </>
      )}
    </FastField>
  );
};

/**
 * Enhanced Formik Document Upload Component
 * Provides file upload functionality with real-time progress tracking and validation
 *
 * SUPPORTED FILE TYPES (up to 100MB each):
 * - Documents: PDF, Word (.doc, .docx), PowerPoint (.ppt, .pptx), Excel (.xls, .xlsx), Text, CSV
 * - Images: JPEG, PNG, GIF
 * - Compressed: ZIP, RAR, 7Z, TAR, GZIP, BZIP2, XZ
 *
 */
export const FormikDocumentUpload = ({
  name,
  helperText,
  accept = '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png,.gif,.zip,.rar,.7z,.tar,.gz,.bz2,.xz',
  multiple = true,
  maxFiles,
  required = false,
  isAdminUpload = false,
  validateFiles = true,
  ...rest
}) => {
  const { uploadFile, uploadMultipleFiles, uploading, validateFile } =
    useFileUpload(isAdminUpload, name);

  return (
    <div style={{ width: '100%' }}>
      <Field name={name}>
        {({ field, form: { errors, touched, setFieldValue } }) => {
          const currentFiles = field.value || [];
          const filesArray = Array.isArray(currentFiles)
            ? currentFiles
            : [currentFiles].filter(Boolean);

          // Determine upload behavior based on props
          const allowMultiple =
            multiple !== false && (maxFiles === undefined || maxFiles > 1);

          const handleFileUpload = async (newFiles) => {
            const filesToUpload = Array.isArray(newFiles)
              ? newFiles
              : [newFiles];

            // Check if files were actually provided (handles duplicate detection)
            if (
              !newFiles ||
              (Array.isArray(newFiles) && newFiles.length === 0)
            ) {
              toast.warning(
                "You've already uploaded this file. Would you like to replace it?",
                {
                  position: 'top-center',
                }
              );
              return;
            }

            // For single file uploads, check if same file already exists
            if (
              !allowMultiple &&
              filesArray.length > 0 &&
              filesToUpload.length > 0
            ) {
              const existingFile = filesArray[0];
              const newFile = filesToUpload[0];

              // Check if it's the same file (by name and size)
              if (
                existingFile.name === newFile.name &&
                existingFile.size === newFile.size
              ) {
                toast.info('This file is already uploaded', {
                  position: 'top-center',
                });
                return;
              }
            }

            // Client-side validation before upload (if enabled)
            if (validateFiles) {
              const validationErrors = [];
              const validatedFiles = [];

              for (const file of filesToUpload) {
                const validation = validateFile(file);
                if (!validation.isValid) {
                  validationErrors.push(`${file.name}: ${validation.error}`);
                } else {
                  validatedFiles.push(file);
                  // Log warnings if any
                  validation.warnings.forEach((warning) => {
                    console.warn(
                      `File validation warning for ${file.name}:`,
                      warning
                    );
                  });
                }
              }

              // Show validation errors if any
              if (validationErrors.length > 0) {
                toast.error(
                  `File validation failed:\n${validationErrors.join('\n')}`,
                  {
                    position: 'top-center',
                  }
                );
                return;
              }

              // Continue with validated files only
              filesToUpload.length = 0;
              filesToUpload.push(...validatedFiles);
            }

            try {
              const uploadedFiles =
                filesToUpload.length === 1
                  ? [
                      await uploadFile(filesToUpload[0], null, !validateFiles),
                    ].filter(Boolean) // Skip validation if already done
                  : await uploadMultipleFiles(filesToUpload);

              if (uploadedFiles.length > 0) {
                const updatedFiles = allowMultiple
                  ? [...filesArray, ...uploadedFiles]
                  : uploadedFiles.slice(0, 1);

                // Respect maxFiles limit
                const finalFiles =
                  maxFiles && updatedFiles.length > maxFiles
                    ? updatedFiles.slice(-maxFiles)
                    : updatedFiles;

                setFieldValue(name, finalFiles);
                // Note: Success message is already shown by useFileUpload hook
              }
            } catch (error) {
              console.error('Upload failed:', error);
              // Note: Error message is already shown by useFileUpload hook
            }
          };

          const handleFileDelete = (fileToDelete) => {
            const remainingFiles = filesArray.filter((file) => {
              const fileId = file.id || file._id;
              const fileName = file.name || file.original_name;
              const targetId = fileToDelete.id || fileToDelete._id;
              const targetName =
                fileToDelete.name || fileToDelete.original_name;

              return fileId !== targetId && fileName !== targetName;
            });

            setFieldValue(name, remainingFiles);
          };

          return (
            <FileUploader
              value={filesArray}
              onUpload={handleFileUpload}
              onFileDelete={handleFileDelete}
              accept={accept}
              helperText={(touched[name] && errors[name]) || helperText}
              error={!!(touched[name] && errors[name])}
              fullWidth
              dropAreaHeight={110}
              multiple={allowMultiple}
              maxFiles={allowMultiple ? maxFiles : 1}
              required={required}
              disabled={uploading}
              {...rest}
            />
          );
        }}
      </Field>

      {/* Upload progress indicator */}
      <UploadProgress fieldName={name} />
    </div>
  );
};

export { FormikImageUploadWithPreview } from './FormikImageUploadWithPreview';
